import { format } from 'date-fns'
import { TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'

import Chip from '../common/chip'
import { AuthContext } from '../../contexts'
import { requestAccessToken } from '../../api/auth.api'
import DangerIcon from '../../assets/icons/danger-icon.svg'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import WarningIcon from '../../assets/icons/warning-icon.svg'
import { FormError, Payment, UserSessionType } from '../../types'
import { getPaymentsWithDuplicateReference } from '../../api/app-queries.api'
import { handleSyncDataToLocalDB, normalizePaymentData, numberWithCommas } from '../../utils'

export const PaymentReferenceNoveltyForm = forwardRef(({ errors, paymentData, userSession }: { errors: FormError; paymentData: Payment | Partial<Payment> | null; userSession: UserSessionType }, ref) => {
  const { isAuth } = useContext(AuthContext)
  const { t } = useTranslation(['payments-to-review-management', 'common'])

  const [newPaymentReference, setNewPaymentReference] = useState<string>('')
  const [paymentReferenceNoveltiesData, setPaymentReferenceNoveltiesData] = useState<
    Array<{
      message: string
      severity: string
    }>
  >([])
  const [duplicatePaymentReference, setDuplicatePaymentReference] = useState<boolean>(false)
  const [duplicatePaymentReferenceCustomerName, setDuplicatePaymentReferenceCustomerName] = useState<string>('')
  const [duplicatePaymentReferenceData, setDuplicatePaymentReferenceData] = useState<Payment | Partial<Payment> | null>(null)

  useImperativeHandle(
    ref,
    () => {
      return {
        newPaymentReference
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newPaymentReference]
  )

  useEffect(() => {
    if (paymentData && Array.isArray(paymentData.novelties)) {
      const paymentReferenceNoveltiesDataToDisplay = []

      for (const element of paymentData.novelties) {
        const paymentNovelty = element
        const paymentReferenceNoveltyToDisplayData = {
          message: '',
          severity: ''
        }

        if (paymentNovelty.type === 'empty-payment-reference') {
          paymentReferenceNoveltyToDisplayData.severity = 'danger'
          paymentReferenceNoveltyToDisplayData.message = t('noveltiesDescription.emptyPaymentReference')

          paymentReferenceNoveltiesDataToDisplay.push(paymentReferenceNoveltyToDisplayData)
        }

        if (paymentNovelty.type === 'duplicate-payment-reference') {
          paymentReferenceNoveltyToDisplayData.severity = 'danger'
          paymentReferenceNoveltyToDisplayData.message = t('noveltiesDescription.duplicatePaymentReference')

          setDuplicatePaymentReference(true)
          paymentReferenceNoveltiesDataToDisplay.push(paymentReferenceNoveltyToDisplayData)
        }
      }

      setPaymentReferenceNoveltiesData(paymentReferenceNoveltiesDataToDisplay)
    }

    if (paymentData?.referenceNumber) {
      const paymentReferenceToDisplay = handleGetPaymentReferenceToDisplay(paymentData.referenceNumber)
      setNewPaymentReference(paymentReferenceToDisplay)

      handleGetDuplicatePaymentReference()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData])

  const handleGetPaymentReferenceToDisplay = (paymentReference: string) => {
    if (userSession?.role.indexOf('-client') === -1 && paymentReference !== 'null' && paymentReference !== 'undefined') {
      return String(paymentReference)
    }

    if (userSession?.role.indexOf('-client') > -1 && paymentReference?.split('-')[1] !== 'null' && paymentReference?.split('-')[1] !== 'undefined') {
      return String(paymentReference?.split('-')[1])
    }

    return ''
  }

  const handleGetDuplicatePaymentReference = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsWithDuplicateReference(String(paymentData?._id), accessToken)

      const paymentToAdd = normalizePaymentData(data.paymentsData[0])
      await handleSyncDataToLocalDB('payments', paymentToAdd)

      setDuplicatePaymentReferenceData(paymentToAdd)
      setDuplicatePaymentReferenceCustomerName(String(data.paymentsData[0].customerData.registeredFullName))
    } catch (error) {
      console.log('Error handleGetDuplicatePaymentReference > ', error)
    }
  }

  return (
    <div
      style={{ borderBottom: 'none' }}
      className="document-findings-edit-bank-account-row-container"
    >
      {Array.isArray(paymentReferenceNoveltiesData) && paymentReferenceNoveltiesData.length > 0 && (
        <div className="novelty-modal-content-container">
          {paymentReferenceNoveltiesData.map(
            (
              paymentReferenceNovelty: {
                message: string
                severity: string
              },
              index: number
            ) => (
              <div
                key={index}
                className="info-data-container"
                style={{ alignItems: 'center', justifyContent: 'center' }}
              >
                <div className="icon-container">
                  <img
                    src={paymentReferenceNovelty.severity === 'danger' ? DangerIcon : WarningIcon}
                    alt="novelty-icon"
                  />
                </div>
                <div
                  className="info-data-text"
                  style={paymentReferenceNovelty.severity === 'danger' ? { color: '#EF4444' } : { color: '#F59E0B' }}
                >
                  {paymentReferenceNovelty.message}
                </div>
              </div>
            )
          )}
        </div>
      )}
      <TextField
        fullWidth
        variant="standard"
        sx={textFieldWhiteBg}
        value={newPaymentReference}
        error={!!errors.errorPaymentReference}
        helperText={errors.errorPaymentReference}
        onChange={(event) => setNewPaymentReference(event.target.value)}
        label={`${t('resolveNoveltyModal.paymentReference.newPaymentReference')}`}
      />
      {duplicatePaymentReference && (
        <div
          style={{ flexDirection: 'column' }}
          className="document-findings-duplicate-payment-container"
        >
          {duplicatePaymentReferenceCustomerName && duplicatePaymentReferenceData ? (
            <>
              <div
                className="subtitle"
                style={{ color: '#F59E0B' }}
              >
                {t('resolveNoveltyModal.paymentReference.duplicateReferenceData')}
              </div>
              <div
                style={{ paddingTop: '0px' }}
                className="document-findings-duplicate-payment-container"
              >
                <div className="document-findings-duplicate-payment-row-item">
                  <div
                    className="subtitle"
                    style={{ textAlign: 'start' }}
                  >
                    <span>{t('resolveNoveltyModal.paymentReference.beneficiaryText')}</span>
                    <br /> {duplicatePaymentReferenceCustomerName}
                  </div>
                  <div
                    className="subtitle"
                    style={{ textAlign: 'start' }}
                  >
                    <span>{t('resolveNoveltyModal.paymentReference.paymentAmount')}</span>
                    <br /> {`$ ${numberWithCommas(Number(paymentData?.amount))} COP`}
                  </div>
                </div>
                <div className="document-findings-duplicate-payment-row-item">
                  {duplicatePaymentReferenceData.paidAt ? (
                    <div
                      className="subtitle"
                      style={{ textAlign: 'start' }}
                    >
                      <span>{t('resolveNoveltyModal.paymentReference.paidAtText')}</span>
                      <br /> {format(duplicatePaymentReferenceData.paidAt, 'dd/MM/yyyy | HH:mm:ss')}
                    </div>
                  ) : (
                    <div
                      className="subtitle"
                      style={{ textAlign: 'start' }}
                    >
                      <span>{t('resolveNoveltyModal.paymentReference.createdAtText')}</span>
                      <br /> {format(String(duplicatePaymentReferenceData.createdAt), 'dd/MM/yyyy | HH:mm:ss')}
                    </div>
                  )}
                  <div
                    className="subtitle"
                    style={{ textAlign: 'start' }}
                  >
                    <span>{t('resolveNoveltyModal.paymentReference.paymentStatusText')}</span>
                    <br />{' '}
                    <Chip
                      status={
                        duplicatePaymentReferenceData.paidAt
                          ? 'success'
                          : !duplicatePaymentReferenceData.paidAt && duplicatePaymentReferenceData.status?.indexOf('for-review') !== -1
                          ? 'error'
                          : !duplicatePaymentReferenceData.paidAt && duplicatePaymentReferenceData.status === 'rejected'
                          ? 'rejected'
                          : 'reviewed'
                      }
                      label={
                        duplicatePaymentReferenceData.paidAt
                          ? t('resolveNoveltyModal.paymentReference.status.paid')
                          : !duplicatePaymentReferenceData.paidAt && duplicatePaymentReferenceData.status?.indexOf('for-review') !== -1
                          ? t('resolveNoveltyModal.paymentReference.status.forRevision')
                          : !duplicatePaymentReferenceData.paidAt && duplicatePaymentReferenceData.status === 'rejected'
                          ? t('resolveNoveltyModal.paymentReference.status.rejected')
                          : t('resolveNoveltyModal.paymentReference.status.reviewed')
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              className="subtitle"
              style={{ color: '#F59E0B' }}
            >
              {t('resolveNoveltyModal.paymentReference.loading')}
            </div>
          )}
        </div>
      )}
    </div>
  )
})
